/*
 * XYNE Redesign — Toast notification system
 * Exposes window.rdToast() for any feature to call.
 *
 * API:
 *   rdToast({ type, message, duration, action })
 *     type     — 'success' | 'error' | 'info' (default: 'info')
 *     message  — string (required)
 *     duration — ms before auto-dismiss (default: 4000; 0 = sticky until manual close)
 *     action   — optional { label, href } for an inline link
 *
 * Returns a controller: { dismiss() }
 *
 * Stack management: max 3 visible; FIFO queue beyond.
 */
(function () {
  'use strict';

  var MAX_VISIBLE = 3;
  var queue = [];
  var visible = [];

  function getContainer() {
    return document.querySelector('[data-rd-toast-container]');
  }

  var ICONS = {
    success: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.6"/><path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    error:   '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.6"/><path d="M12 7v6m0 3.5v.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    info:    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.6"/><path d="M12 11v6m0-9v.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
  };

  function build(opts) {
    var type = opts.type || 'info';
    if (!ICONS[type]) type = 'info';

    var el = document.createElement('div');
    el.className = 'rd-toast rd-toast--' + type;
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');

    var actionHtml = '';
    if (opts.action && opts.action.label && opts.action.href) {
      actionHtml = '<a class="rd-toast__action" href="' + escapeHtml(opts.action.href) + '">' +
                   escapeHtml(opts.action.label) +
                   ' <svg viewBox="0 0 24 24" width="12" height="12" fill="none"><path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
                   '</a>';
    }

    el.innerHTML =
      '<span class="rd-toast__icon">' + ICONS[type] + '</span>' +
      '<div class="rd-toast__body">' +
        '<p class="rd-toast__message">' + escapeHtml(opts.message || '') + '</p>' +
        actionHtml +
      '</div>' +
      '<button type="button" class="rd-toast__close" aria-label="Dismiss">' +
        '<svg viewBox="0 0 24 24" fill="none"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
      '</button>';

    return el;
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = String(s == null ? '' : s);
    return div.innerHTML;
  }

  function processQueue() {
    while (visible.length < MAX_VISIBLE && queue.length > 0) {
      var next = queue.shift();
      show(next);
    }
  }

  function show(item) {
    var container = getContainer();
    if (!container) return;

    var el = build(item.opts);
    container.appendChild(el);

    var dismissed = false;
    var dismissTimer = null;

    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
      el.classList.add('rd-toast--leaving');
      var onEnd = function () {
        if (el.parentNode) el.parentNode.removeChild(el);
        var idx = visible.indexOf(item);
        if (idx >= 0) visible.splice(idx, 1);
        processQueue();
      };
      // Use animationend; fallback timer in case animation is disabled
      el.addEventListener('animationend', onEnd, { once: true });
      setTimeout(onEnd, 600);
    }

    item.el = el;
    item.dismiss = dismiss;
    visible.push(item);

    // Close button
    var closeBtn = el.querySelector('.rd-toast__close');
    if (closeBtn) closeBtn.addEventListener('click', dismiss);

    // Auto-dismiss
    var duration = typeof item.opts.duration === 'number' ? item.opts.duration : 4000;
    if (duration > 0) {
      dismissTimer = setTimeout(dismiss, duration);
      // Pause on hover so users can read longer messages
      el.addEventListener('mouseenter', function () {
        if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
      });
      el.addEventListener('mouseleave', function () {
        if (!dismissed) {
          dismissTimer = setTimeout(dismiss, 1500);
        }
      });
    }
  }

  function rdToast(opts) {
    if (!opts || !opts.message) {
      console.warn('rdToast: message is required');
      return { dismiss: function () {} };
    }

    var item = { opts: opts };
    if (visible.length < MAX_VISIBLE) {
      show(item);
    } else {
      queue.push(item);
    }

    return {
      dismiss: function () {
        if (item.dismiss) item.dismiss();
        else {
          // Still in queue; remove
          var idx = queue.indexOf(item);
          if (idx >= 0) queue.splice(idx, 1);
        }
      }
    };
  }

  // Convenience wrappers
  rdToast.success = function (message, opts) {
    return rdToast(Object.assign({ type: 'success', message: message }, opts || {}));
  };
  rdToast.error = function (message, opts) {
    return rdToast(Object.assign({ type: 'error', message: message }, opts || {}));
  };
  rdToast.info = function (message, opts) {
    return rdToast(Object.assign({ type: 'info', message: message }, opts || {}));
  };

  window.rdToast = rdToast;
})();
